import { t } from '@lingui/macro'
import { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Grid } from '@/@bothrs-ui/web/components/layout/grid/Grid'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H1 } from '@/@bothrs-ui/web/components/typography/Heading'
import { useAuthentication } from '@/modules/auth/hooks/useAuthentication'
import { Loader } from '@/modules/shared/components/loader/Loader'
import { ExamplePatientLogin } from './ExamplePatientLogin'

import type { FC, ReactNode } from 'react'

export type Props = {
  /**
   * Component to show a message configured by the Admin in Healthblocks
   */
  message: ReactNode

  /**
   * Callback when user presses the debug button
   * */
  onDebugPress: () => void

  children: ReactNode
}

/**
 * Component to show the login content for eHealth authentication
 * (initial screen when user is not authenticated)
 */
export const EHealthLoginContent: FC<Props> = ({ message, onDebugPress, children }) => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()

  const { authenticate, authenticatedAndLoading, shouldBeLoading } = useAuthentication()

  // Interaction methods
  // ------------------------------------------------------------------------- /

  const handleLogin = async () => {
    await authenticate()
  }

  // Render
  // ------------------------------------------------------------------------- /

  if (shouldBeLoading || authenticatedAndLoading) {
    return <Loader cancellable />
  }

  return (
    <FlexColumn gap={theme.UI.Spacing.Space6}>
      {/* Title */}
      <H1 onClick={onDebugPress}>
        {t({
          id: 'login.welcome',
        })}
      </H1>

      <FlexColumn gap={theme.UI.Spacing.Space4}>
        {import.meta.env.DEV && (
          <Grid gridTemplateColumns='1fr 1fr' columnGap={theme.UI.Spacing.Space2} rowGap={theme.UI.Spacing.Space2}>
            <ExamplePatientLogin
              credentials={{
                email: 'mikkis.georgios@healthblocks.io',
                password: 'Admintest123456',
              }}
              label='Login als Mikkis'
            />
            <ExamplePatientLogin
              credentials={{
                email: 'jean-francois-tom.specimen@healthblocks.io',
                password: 'Admintest123456',
              }}
              label='Login als Jean François'
            />
            <ExamplePatientLogin
              credentials={{
                email: 'melissa.specimen@healthblocks.io',
                password: 'Admintest123456',
              }}
              label='Login als Melissa'
            />
            <ExamplePatientLogin
              credentials={{
                email: 'jean-francois-tom.specimen@healthblocks.io',
                password: 'Admintest123456',
              }}
              label='Login als Melissa (Met mandaat van Jean François)'
              loginAsMandate={true}
            />
            <ExamplePatientLogin
              credentials={{
                email: 'nora.specimen@healthblocks.io',
                password: 'Admintest123456',
              }}
              label='Login als Nora'
            />
          </Grid>
        )}
        {children}

        {/* Show message configured in Healthblocks by Admin */}
        {message}

        {/* Message Body */}
        <Span type='BodyLargeBold'>
          {t({
            id: 'login.description.login.ehealth',
            message: 'Meld je aan met eHealth',
          })}
        </Span>
        {/* Buttons */}
        <FlexColumn gap={theme.UI.Spacing.Space2}>
          <Button
            variant='Primary'
            isLoading={authenticatedAndLoading}
            disabled={authenticatedAndLoading}
            onClick={handleLogin}
          >
            {t({
              id: 'login.action.login',
              message: 'Meld je aan',
            })}
          </Button>
        </FlexColumn>
      </FlexColumn>
    </FlexColumn>
  )
}
